import React from 'react';
import { Photo } from '../../../../types';
import { PhotoDisplayData } from '../../../../types/PhotoDisplayData';
import { useImageLoader } from '../../../../hooks/useImageLoader';
import { useUIStore } from '../../../../stores/useUIStore';
import { useAuthStore } from '../../../../stores/useAuthStore';
import { PhotoTileFrame } from './PhotoTileFrame';

interface ImagePhotoItemProps {
    photo: Photo;
    photoData?: PhotoDisplayData;
    index: number;
    totalPhotos?: number;
}

const ImagePhotoItem: React.FC<ImagePhotoItemProps> = ({
    photo,
    photoData,
    index,
    totalPhotos,
}) => {
    const { imageUrl, isLoading, hasError } = useImageLoader(photo.url);
    const { openMediaModal } = useUIStore();
    const revalidateSessionAfterMediaError = useAuthStore((state) => state.revalidateSessionAfterMediaError);

    const handleImageClick = () => {
        // Fall back to photo.url so the modal opens even when the image failed to load.
        const urlToOpen = imageUrl || photo.url;
        openMediaModal(urlToOpen, 'image');
    };

    let tileContent: React.ReactNode;
    if (isLoading) {
        tileContent = <div className="event-photo-loading">Loading...</div>;
    } else if (hasError) {
        tileContent = <div className="event-photo-error">Failed to load</div>;
    } else if (imageUrl) {
        tileContent = (
            <img
                src={imageUrl}
                alt={photo.caption ? photo.caption : `Memory ${index + 1}`}
                className="event-photo-img"
                data-testid="photo-image"
                draggable={false}
                decoding="async"
                onError={revalidateSessionAfterMediaError}
            />
        );
    } else {
        tileContent = <div className="event-photo-loading">Loading...</div>;
    }

    return (
        <PhotoTileFrame
            photo={photo}
            photoData={photoData}
            index={index}
            totalPhotos={totalPhotos}
            wrapperClassName="image-wrapper"
            wrapperStyle={{ cursor: 'pointer' }}
            ariaLabel={photo.caption ? `View image: ${photo.caption}` : `View Memory ${index + 1}`}
            onActivate={handleImageClick}
        >
            {tileContent}
        </PhotoTileFrame>
    );
};

export default ImagePhotoItem;
